/**
 * S1 · Style gallery.
 * - Bootstraps guest session on first arrival (idempotent, keyed by program slug).
 * - Loads catalogs; on pick, persists locally + PATCHes guest session via re-create-flow.
 *   NOTE: we do not have a "patch guest session" RPC; the guest session already carries
 *   optional style_id from creation. We recreate the session on the first pick if not yet
 *   created, or store the pick locally and it will be applied at graduation. This matches
 *   the wiring plan — server-side style hold happens no later than S3.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ensureGuestSession, readAttribution, saveStylePick, readStylePick, readSelfiePath, recordFunnelEvent } from "@/lib/consumer-session";
import { useRepeatProfile } from "@/lib/repeat-flow";
import { useConsumerFlow } from "../ConsumerFlow";
import { FunnelPage, FunnelContainer, FunnelHeader, Display, Lede, Pill, StickyBar, PrimaryButton, CardFace } from "@/components/consumer/chrome";
import { STYLE_CATEGORIES, StyleCategory, tileMeta } from "@/lib/style-art";
import { cn } from "@/lib/utils";
import { useSiteContent } from "@/hooks/useSiteContent";
import { S1_SECTION, S1_CONTENT, S1_DEFAULTS } from "@/content/screens/s1";

interface Tile {
  style_id: string;
  name: string;
  category: StyleCategory;
  color: string;
  /** B3/B4 · allocated preview image, or null → name placeholder. */
  art: string | null;
  /** Theme + default sub-style — the prompt layers this tile resolves to. */
  theme_id: string | null;
  sub_style_id: string | null;
}

export default function S1StyleGallery() {
  const { program } = useConsumerFlow();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(() => readStylePick(program.slug)?.style_id ?? null);
  const [filter, setFilter] = useState<StyleCategory | "all">("all");
  const [layout, setLayout] = useState<"grid" | "swipe">("grid");
  const repeat = useRepeatProfile(program.slug);
  const { t } = useSiteContent(S1_SECTION, S1_DEFAULTS);

  useEffect(() => {
    (async () => {
      try {
        await ensureGuestSession(program.slug, readAttribution(program.slug));
        recordFunnelEvent(program.slug, "landed", program.program_id);
      } catch (e: any) {
        setError(e?.message || t("s1.errors.loadFailed"));
      } finally {
        setLoading(false);
      }
    })();
  }, [program.id, program.slug]); // eslint-disable-line react-hooks/exhaustive-deps

  const tiles: Tile[] = useMemo(() => {
    // Tiles are THEME rows exposed by the programme (one authored list, derived
    // here). Name, category, colour and picture all travel on the row — there is
    // no local catalogue to fall back to, so an empty programme list renders
    // empty rather than a stale copy.
    return (program.styles || []).map((r) => {
      const m = tileMeta(r);
      return {
        style_id: m.slug,
        name: m.name,
        category: m.category,
        color: m.color,
        // B4 · null keeps CardFace's graceful name placeholder.
        art: m.art,
        theme_id: m.themeId,
        sub_style_id: m.subStyleId,
      };
    });
  }, [program.styles]);


  const visible = useMemo(
    () => tiles.filter((t) => filter === "all" || t.category === filter),
    [tiles, filter],
  );

  const pick = (styleId: string) => {
    const tile = tiles.find((t) => t.style_id === styleId);
    setSelected(styleId);
    // The pick carries the resolved layers from the moment it is stored, so the
    // generator never consults a slug map.
    saveStylePick(program.slug, { style_id: styleId, hobby_id: tile?.theme_id ?? null, sub_style_id: tile?.sub_style_id ?? null });
    recordFunnelEvent(program.slug, "style_picked", program.program_id, { style_id: styleId, theme_id: tile?.theme_id ?? null });
  };

  const disabled = useMemo(() => !selected, [selected]);
  const countLabel = !selected ? t("s1.actions.countPrompt") : t("s1.actions.countSelected");

  // Lane 2 — the gallery is always shown, but repeat buyers get owned styles
  // marked, a shorter step count, and a CTA that skips the selfie when their
  // avatar is already on file.
  const owned = useMemo(() => new Set(repeat.ownedStyleIds), [repeat.ownedStyleIds]);
  /**
   * Changing the style must change ONE thing. Anyone already IDENTIFIED (a live
   * session) who already has a face on file — either bound to their profile or
   * staged in this funnel run — continues to the same-face fork, where they can
   * reuse it or retake. They are never sent back through capture + email + OTP,
   * which is what "going back to change the style" used to do.
   */
  const faceOnFile = repeat.storedSelfiePath || (repeat.isIdentified ? readSelfiePath(program.slug) : null);
  const reuseFace = repeat.isIdentified && !!faceOnFile;
  const continueTo = reuseFace ? "../same-face" : "../selfie";
  const continueLabel = reuseFace ? t("s1.actions.continueReuse") : t("s1.actions.continueNew");

  return (
    <FunnelPage testId="s1-gallery">
      <FunnelContainer size="wide">
        <FunnelHeader
          backTo={repeat.isRepeat ? "/account" : "/"}
          backLabel={repeat.isRepeat ? t("s1.header.backLabelRepeat") : t("s1.header.backLabelNew")}
          step={repeat.isRepeat ? t("s1.header.stepRepeat") : t("s1.header.stepNew")}
        />
        <Display className="mb-3 max-w-[15ch]">
          {repeat.isRepeat ? t("s1.header.titleRepeat") : t("s1.header.titleNew")}
        </Display>
        <Lede className="mb-1 max-w-[46ch]">
          {repeat.isRepeat
            ? t("s1.header.subtitleRepeat")
            : t("s1.header.subtitleNew")}
        </Lede>

      </FunnelContainer>

      <div className="sticky top-0 z-[2] bg-paper/[0.94] backdrop-blur-[10px]">
        {/* N4 · layout toggle ABOVE the filters. Side by side, the scrolling
            filter row ran underneath it. */}
        <FunnelContainer size="wide" className="flex flex-col gap-2.5 !pt-3.5 pb-3.5 md:!pt-4 md:pb-4">
          <div className="flex justify-end">
          <div className="flex flex-none gap-0.5 rounded-full bg-foreground/[0.07] p-[3px]">
            <button
              type="button"
              title="Grid"
              aria-label={t("s1.filters.gridLabel")}
              onClick={() => setLayout("grid")}
              data-testid="s1-gallery-layout-grid"
              className={cn("grid h-[38px] w-10 place-items-center rounded-full", layout === "grid" ? "bg-white shadow-[0_1px_3px_rgba(28,32,48,0.16)]" : "bg-transparent")}
            >
              <svg viewBox="0 0 20 20" className="h-4 w-4">
                <rect x="2" y="2" width="7" height="7" rx="1.6" fill={layout === "grid" ? "#1C2030" : "rgba(28,32,48,0.45)"} />
                <rect x="11" y="2" width="7" height="7" rx="1.6" fill={layout === "grid" ? "#1C2030" : "rgba(28,32,48,0.45)"} />
                <rect x="2" y="11" width="7" height="7" rx="1.6" fill={layout === "grid" ? "#1C2030" : "rgba(28,32,48,0.45)"} />
                <rect x="11" y="11" width="7" height="7" rx="1.6" fill={layout === "grid" ? "#1C2030" : "rgba(28,32,48,0.45)"} />
              </svg>
            </button>
            <button
              type="button"
              title="Swipe"
              aria-label={t("s1.filters.swipeLabel")}
              onClick={() => setLayout("swipe")}
              data-testid="s1-gallery-layout-swipe"
              className={cn("grid h-[38px] w-10 place-items-center rounded-full", layout === "swipe" ? "bg-white shadow-[0_1px_3px_rgba(28,32,48,0.16)]" : "bg-transparent")}
            >
              <svg viewBox="0 0 20 20" className="h-4 w-4">
                <rect x="1" y="4" width="4" height="12" rx="1.4" fill={layout === "swipe" ? "#1C2030" : "rgba(28,32,48,0.45)"} />
                <rect x="6.5" y="2" width="7" height="16" rx="1.8" fill="#1C2030" />
                <rect x="15" y="4" width="4" height="12" rx="1.4" fill={layout === "swipe" ? "#1C2030" : "rgba(28,32,48,0.45)"} />
              </svg>
            </button>
          </div>
          </div>
          <div className="flex min-w-0 gap-2 overflow-x-auto [scrollbar-width:none]" data-testid="s1-gallery-filters">
            {STYLE_CATEGORIES.map((c) => (
              <Pill key={c.id} active={filter === c.id} onClick={() => setFilter(c.id)} data-testid={`s1-gallery-filter-${c.id}`}>
                {c.label}
              </Pill>
            ))}
          </div>
        </FunnelContainer>
      </div>

      {loading && <FunnelContainer size="wide"><p className="text-foreground/60" data-testid="s1-gallery-loading">{t("s1.body.loading")}</p></FunnelContainer>}
      {error && <FunnelContainer size="wide"><p className="text-[#EC1314]" data-testid="s1-gallery-error">{error}</p></FunnelContainer>}

      <FunnelContainer size="wide" className="!pt-2">
        {layout === "grid" ? (
          <ul
            className="grid gap-x-3.5 gap-y-[22px] pt-2 [grid-template-columns:repeat(auto-fill,minmax(150px,1fr))] md:[grid-template-columns:repeat(auto-fill,minmax(170px,1fr))]"
            data-testid="s1-gallery-list"
          >
            {visible.map((tile) => (
              <li key={tile.style_id}>
                <button
                  type="button"
                  onClick={() => pick(tile.style_id)}
                  data-testid={`s1-gallery-tile-${tile.style_id}`}
                  data-selected={selected === tile.style_id}
                  data-owned={owned.has(tile.style_id) || undefined}
                  className="flex w-full flex-col gap-2.5 text-left"
                >
                  <span className="relative block">
                    <CardFace
                      color={tile.color}
                      art={tile.art}
                      alt={tile.name}
                      className={cn(
                        "transition-transform hover:-translate-y-1",
                        selected === tile.style_id && "outline outline-[3px] outline-offset-[3px]",
                        owned.has(tile.style_id) && selected !== tile.style_id && "opacity-70",
                      )}
                      style={selected === tile.style_id ? ({ outlineColor: "#8834B2" } as any) : undefined}
                    />
                    {owned.has(tile.style_id) ? (
                      <span
                        data-testid={`s1-gallery-owned-${tile.style_id}`}
                        className="absolute left-2 top-2 rounded-full bg-ink/85 px-2 py-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-paper"
                      >
                        {t("s1.body.ownedBadge")}
                      </span>
                    ) : null}
                  </span>
                  <span className={cn("text-[14px] font-semibold tracking-[-0.01em]", selected === tile.style_id && "text-grape")}>
                    {tile.name}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <div className="flex gap-3.5 overflow-x-auto pb-2 pt-2 [scroll-snap-type:x_mandatory] [scrollbar-width:none]" data-testid="s1-gallery-list">
            {visible.map((tile) => (
              <button
                key={tile.style_id}
                type="button"
                onClick={() => pick(tile.style_id)}
                data-testid={`s1-gallery-tile-${tile.style_id}`}
                data-selected={selected === tile.style_id}
                data-owned={owned.has(tile.style_id) || undefined}
                className="flex w-[240px] flex-none flex-col gap-2.5 text-left [scroll-snap-align:center]"
              >
                <span className="relative block">
                  <CardFace
                    color={tile.color}
                    art={tile.art}
                    alt={tile.name}
                    className={cn(
                      selected === tile.style_id && "outline outline-[3px] outline-offset-[3px]",
                      owned.has(tile.style_id) && selected !== tile.style_id && "opacity-70",
                    )}
                    style={selected === tile.style_id ? ({ outlineColor: "#8834B2" } as any) : undefined}
                  />
                  {owned.has(tile.style_id) ? (
                    <span className="absolute left-2 top-2 rounded-full bg-ink/85 px-2 py-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-paper">
                      {t("s1.body.ownedBadge")}
                    </span>
                  ) : null}
                </span>
                <span className={cn("text-[14px] font-semibold tracking-[-0.01em]", selected === tile.style_id && "text-grape")}>
                  {tile.name}
                </span>
              </button>
            ))}
          </div>
        )}
      </FunnelContainer>

      <StickyBar>
        <span className="order-1 flex-1 basis-full text-[15px] font-medium text-foreground/[0.78] md:basis-auto" data-testid="s1-gallery-count">
          {selected && owned.has(selected) ? t("s1.actions.countOwned") : countLabel}
        </span>
        <PrimaryButton
          disabled={disabled}
          onClick={() => navigate(continueTo)}
          data-testid="s1-gallery-continue"
          className="order-2 basis-full md:w-auto md:basis-auto"
        >
          {continueLabel}
        </PrimaryButton>
      </StickyBar>

    </FunnelPage>
  );
}
